import { InteractiveSide as Bridge } from '@megogo-interactive/bridge';
import {
  BehaviorSubject, distinctUntilChanged, filter, fromEventPattern, map, mergeWith, scan, share, Subject, tap
} from 'rxjs';
import scopeLogger from '#/lib/scope-logger.js';

/**
 * @typedef {Object} PositionUpdate
 * @property {number} start_time
 * @property {number} position
 * @property {number} duration
 * @property {number} position_delta
 * @property {string?} content_type
 * @property {string?} video_state
 */

/**
 * @type {PositionUpdate}
 */
const initialPosition = {
  start_time: 0,
  position: 0,
  duration: 0,
  position_delta: 0,
  content_type: null,
  video_state: null,
};
const emptyContext = {};

let withDebugDecorator = fn => fn;

const upstreaming = [share()];

if (import.meta.env.VITE_APP_MESSAGE_DEBUG) {
  /* Upstream message, e.g. "received" */
  upstreaming.unshift(
    tap((message) => {
      console.debug(`%c⤋ HOST message`, 'color: red', message);
    }),
  );
  /* Downstream messages, e.g. "sent" */
  withDebugDecorator = (fn) => (type, data) => {
    console.debug(`%c⤊ ${type}`, 'color: green', data);
    return fn(type, data);
  }
}
const console = scopeLogger('RMI:HostGateway');

export default class HostGateway {
  #context$ = new BehaviorSubject(emptyContext);
  #upstream$ = new Subject();
  #message$ = this.#upstream$.pipe(...upstreaming);

  #bridge = new Bridge({
    sender: {
      send: withDebugDecorator(this.#selectedSender),
    },
  });

  #suspendPositionUsage = import.meta.env.PROD; /* hacky solution for keep working on hot reload in dev mode */
  position$ = new BehaviorSubject(initialPosition);

  constructor() {
    this.#bridge.onAction = (action) => {
      /*
       site give `action`:
        {element_id, event_type, event: {type, target: {id, value}}}
       mob give:
        {element_id, event_type}
       */
      this.#upstream$.next({
        cmd: action.event_type,
        payload: action,
        ...action, // for backward compatibility, almost in remote control
      });
    };

    this.#listenCreate();
    this.#listenTimeUpdate();
    this.#listenStartStop();
    this.#listenPause();
    this.#listenResume();
    this.#listenZoneResize();

    this.createControl = this.createControl.bind(this);
    this.removeControl = this.removeControl.bind(this);
    this.requestForeground = this.requestForeground.bind(this);
    this.releaseForeground = this.releaseForeground.bind(this);

    this.releaseFocus = this.#bridge.releaseFocus;
    this.sendEvent = this.#bridge.sendEvent;
    this.trackEvent = this.#bridge.trackEvent;
  }

  sendNotify = this.#bridge.sendNotify;

  get message$() {
    return this.#message$;
  }

/*
  get position$() {
    return this.#position$.pipe(
      filter(() => !this.suspendPositionUsage),
      shareReplay(1),
    );
  }
*/

  /**
   * Return timestamp of position for current video
   * @return {number}
   */
  get position() {
    return this.position$.value.start_time + this.position$.value.position;
  }

  /**
   * Return the current position in the video relative to the start.
   * @return {number}
   */
  get relativePosition() {
    return this.position$.value.position;
  }

  #listenCreate() {
    fromEventPattern(
      (handler) => (this.#bridge.onCreate = handler),
      () => (this.#bridge.onCreate = null),
    ).subscribe((message) => {
      this.#upstream$.next({
        cmd: 'create',
        payload: message,
      });
    });
  }

  #listenStartStop() {
    const start$ = fromEventPattern(
      (handler) => (this.#bridge.onStart = handler),
      () => (this.#bridge.onStart = null),
    ).pipe(
      filter(({content_type, video_id}) => typeof content_type === 'string' && content_type !== '' && Boolean(video_id)),
      /* make callback in every case, regardless it is really start on new context
 or the fake re-start due to host-app specificity */
      tap(() => this.sendNotify.started()),
      map((message) => ({ ...message, cmd: 'start' })),
      /* next one necessary patch for mobile version */
      map((message) => ({ object_id: message.channel_id || message.video_id, ...message })),
      map((message) => {
        message.start_time ??= 0; // ensure start_time is defined
        message.duration ??= Infinity; // ensure duration is defined
        return message;
      }),
    );

    const stop$ = fromEventPattern(
      (handler) => (this.#bridge.onStop = handler),
      () => (this.#bridge.onStop = null),
    ).pipe(
      tap(() => this.sendNotify.stopped()),
      map((message) => ({ ...message, cmd: 'stop' }))
    );

    start$
      .pipe(
        mergeWith(stop$),
        tap(({ cmd, ...message }) => {
          console.debug(`Received %c${cmd.toUpperCase()}%c message`, 'font-weight: bold', 'font-weight: normal', message);
        }),
        /* passing only really new start */
        distinctUntilChanged(
          (prev, next) =>
            prev.cmd === next.cmd &&
            prev.content_type === next.content_type &&
            prev.video_id === next.video_id &&
            prev.virtual_id === next.virtual_id,
        ),
        tap(({ cmd }) => {
          this.#suspendPositionUsage = cmd === 'stop';
        }),
        tap(({ cmd, ...message }) => {
          /* force types on incoming message */
          const payload = {
            ...message,
            channel_id: message.channel_id?.toString(),
            video_id: message.video_id?.toString(),
            object_id: message.object_id?.toString(),
            root_id: message.root_id?.toString(),
            isLive: ['live', 'live_dvr'].includes(message.content_type)
          };

          this.#upstream$.next({
            cmd,
            payload,
          });

          if (cmd === 'start') {
            this.position$.next({
              ...initialPosition,
              start_time: message.start_time,
              duration: message.duration,
            });
          }

        }),
      )
      .subscribe();
  }

  #listenPause() {
    fromEventPattern(
      (handler) => (this.#bridge.onPause = handler),
      () => (this.#bridge.onPause = null),
    ).subscribe((message) => {
      this.#upstream$.next({
        cmd: 'pause',
        payload: message,
      });
      this.sendNotify.paused();
    });
  }

  #listenResume() {
    fromEventPattern(
      (handler) => (this.#bridge.onResume = handler),
      () => (this.#bridge.onResume = null),
    ).subscribe((message) => {
      this.#upstream$.next({
        cmd: 'resume',
        payload: message,
      });
      this.sendNotify.resumed();
    });
  }

  #listenTimeUpdate() {
    fromEventPattern(
      (handler) => (this.#bridge.onTimeUpdate = handler),
      () => (this.#bridge.onTimeUpdate = null),
    )
      .pipe(
        /* suspend update position while not started */
        filter(() => !this.#suspendPositionUsage),
        // too many updates on iOS during seek, up to 6 times per second from site
        // sampleTime(500),
/*
        map((message) => {
          // rude patch for RMI-939,
          // TODO: resole getting live VOD position problem:
          //  STV - completely out of work,
          //  the site has the same ugly solution
          if (message.content_type === 'live') {
            message.position = Math.round(Date.now() / 1e3);
          }
          return message;
        }),
*/
        map((message) => {
          message.position = Math.floor(message.position);
          return message;
        }),
        distinctUntilChanged((prev, next) => prev.position === next.position),
        scan((prev, message) => {
          return {
            ...message,
            position_delta: message.position - prev.position,
          };
        }, initialPosition),
/*
        tap((message) => {
          if (this.#suspendPositionUsage) {
            console.warn('Position update while suspended', message);
            // return;
          }
        }),
*/
      )
      .subscribe((message) => {
        this.position$.next({
          ...this.position$.value,
          ...message,
        });
/*
        if (import.meta.env.DEV) {
          // let content = `${message.video_state} ${message.content_type} at ${}`;
          console.debug(
            `%c⤋ Update position`,
            'color:red; font-weight:bold',
            readableDate(
              new Date(message.position * 1e3),
            ),
          );
        }
*/
      });
  }

  #listenZoneResize() {
    fromEventPattern(
      (handler) => (this.#bridge.onZoneResize = handler),
      () => (this.#bridge.onZoneResize = null),
    ).subscribe((message) => {
      this.#upstream$.next({
        cmd: 'zone-resize',
        payload: message,
      });
    });
  }
  log() {
    throw new Error('Using method `log` not allowed.');
    if (import.meta.env.DEV) {
      // useless
      this.#upstream$.subscribe({
        next(message) {
          console.debug(`%c⤋ HOST message`, 'color: red', message);
        },
      });
      /*
      const [positionUpdate$, other$] = partition(this.#message$, (message) => {
        return (
          message.topic === '/host-action' &&
          message.payload.type === 'video-position'
        );
      });
*/
      /*
      positionUpdate$.subscribe((message) => {
        const obj = message.payload.data;
        let content = `${obj.video_state} ${obj.content_type} at ${readableDate(
          new Date(obj.position * 1e3),
        )}`;
        console.debug(
          `%c⤋ ${message.topic}`,
          'color:red; font-weight:bold',
          content,
        );
      });
*/
      /*
      other$.subscribe((message) => {
        console.log(
          `%c⤋ ${message.topic}`,
          'color:red; font-weight:bold',
          message.payload,
        );
      });
*/
    }
  }

  createControl(controlDescription) {
    const { events = {}, element } = controlDescription;
    const description = {
      // element: sanitizeElementDesc(element),
      element: element,
      events: Object.keys(events),
    };
    this.#bridge.showElement(description);
  }

  removeControl(description) {
    this.#bridge.hideElement(description);
  }

  requestForeground({
    onCloseRequest: _onCloseRequest,
    mode = 'player',
    parameters,
  } = {}) {
    const description = {
      element: {
        element_id: 'fullscreen',
        element_type: 'fullscreen',
        mode,
        parameters,
      },
      events: ['system'],
    };
    this.#bridge.showElement(description);
  }

  releaseForeground(parameters) {
    const controlDescription = {
      element: {
        element_id: 'fullscreen',
        element_type: 'fullscreen',
        parameters,
      },
    };
    this.#bridge.hideElement(controlDescription);
  }

  requestNewToken() {
    this.sendNotify.error({
      fatal: false,
      message: 'Unauthorized',
      code: 401,
    });
  }

  get #selectedSender() {
    switch (true) {
      /*
      May accept:
      webkit.messageHandlers.megogoApp.postMessage("app:started")
      webkit.messageHandlers.megogoApp.postMessage({type: "app:started"})
      webkit.messageHandlers.megogoApp.postMessage({type: "app:started", data: null})
      webkit.messageHandlers.megogoApp.postMessage({type: "app:started", data: {foo: "bar"}})
       */
      case typeof window.webkit?.messageHandlers?.megogoApp?.postMessage ===
        'function': {
        return (type, data) =>
          window.webkit.messageHandlers.megogoApp.postMessage({
            type,
            data,
          });
      }

      case typeof window.androidClient?.postAndroidMessage === 'function': {
        return (type, data) =>
          window.androidClient.postAndroidMessage(type, JSON.stringify(data));
      }

      case typeof window.parent?.postMessage === 'function': {
        return (type, data) => {
          window.parent.postMessage({ type, data }, '*');
        };
      }

      default:
        return (type, _data) => {
          console.warn('undelivered message', type);
        };
    }
  }
}
